import React, { useState } from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { useFieldAttributesQuery } from 'components/hooks/fieldAttributeQueries';

import PersonalInfo from 'components/application/PersonalInfo';

import {
  findCurrentFieldValue,
  teamNamesForCurrentPersonnel,
} from 'components/helpers/personnel';

const moreBreakpoint = 3;

export default function PersonnelDetails({
  currentPersonnel,
  defaultDetails,
  isAllVisible = false,
  isCompact = false,
  teams = {},
}) {
  const {
    data: domainFieldAttributes,
    isSuccess: isDomainFieldAttributesSuccess,
  } = useFieldAttributesQuery();

  const [showAllFields, setShowAllFields] = useState(isAllVisible);
  const [showAllTeams, setShowAllTeams] = useState(isAllVisible);

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'personnel.PersonnelDetails',
  });

  const { t: i18nTShared } = useTranslation('common', {
    keyPrefix: 'shared',
  });

  const allDetails = defaultDetails
    .filter((field) => field.text)
    .concat(
      (domainFieldAttributes?.data || []).reduce(
        (customDetails, fieldAttribute) => {
          const value = findCurrentFieldValue({
            fieldAttribute,
            fieldOptions: domainFieldAttributes.included.fieldOption,
            fieldValues: currentPersonnel.fieldValues,
          });

          if (value) {
            customDetails.push({
              label: fieldAttribute.attributes.name,
              text: value,
            });
          }

          return customDetails;
        },
        [],
      ),
    );

  const visibleDetails =
    allDetails.length < moreBreakpoint || showAllFields ?
      allDetails
    : allDetails.slice(0, moreBreakpoint);

  const teamNames = teamNamesForCurrentPersonnel(currentPersonnel, teams);

  const visibleTeamNames =
    showAllTeams ? teamNames : teamNames.slice(0, moreBreakpoint);

  const hasNoDetails = allDetails.length === 0 && teamNames.length === 0;

  return (
    <div className={classNames(!isCompact && 'tw-px-5 tw-py-6')}>
      {allDetails.length > 0 && (
        <>
          {isDomainFieldAttributesSuccess &&
            visibleDetails.map((dataPoint, index) => (
              <PersonalInfo
                className='tw-mb-2'
                copyable={dataPoint.label === i18nTShared('email')}
                hideIcon={true}
                isCompact={isCompact}
                key={index}
                label={dataPoint.label}
                preserveWhitespace={true}
                text={dataPoint.text}
              />
            ))}
          {allDetails.length > moreBreakpoint && !showAllFields && (
            <div className='tw-mb-2 tw-flex tw-items-center'>
              <button
                className='app-link tw-mb-2 tw-bg-transparent tw-text-blue-500 hover:tw-text-blue-300 active:tw-text-blue-300'
                onClick={() => setShowAllFields(true)}
              >
                {i18nTShared('showAllWithMoreCount', {
                  count: allDetails.length - moreBreakpoint,
                })}
              </button>
            </div>
          )}
        </>
      )}
      {teamNames.length > 0 && (
        <PersonalInfo
          className='tw-mb-2'
          copyable={false}
          hideIcon={true}
          isCompact={isCompact}
          label={i18nTShared('teams')}
          preserveWhitespace={true}
          text={visibleTeamNames.join('\n')}
        />
      )}
      {teamNames.length > moreBreakpoint && !showAllTeams && (
        <div className='tw-mb-2'>
          <button
            className='app-link tw-mb-2 tw-bg-transparent tw-text-blue-500 hover:tw-text-blue-300 active:tw-text-blue-300'
            onClick={() => setShowAllTeams(true)}
          >
            {i18nTShared('showAllWithMoreCount', {
              count: teamNames.length - moreBreakpoint,
            })}
          </button>
        </div>
      )}
      {hasNoDetails && (
        <div className='popup__body-message tw-text-grey-500'>
          {i18nT('noDetails')}
        </div>
      )}
    </div>
  );
}

PersonnelDetails.propTypes = {
  currentPersonnel: PropTypes.object.isRequired,
  defaultDetails: PropTypes.array,
  isAllVisible: PropTypes.bool,
  isCompact: PropTypes.bool,
  teams: PropTypes.object,
};
